import * as Schema from "effect/Schema";
import { ChevronUpIcon, EllipsisIcon, PanelLeftCloseIcon, PanelLeftIcon } from "lucide-react";
import { AnimatedWorkflowIcon } from "./AnimatedWorkflowIcon";

import { NavRailItem, RAIL_ITEM_BASE_CLASS, RailHoverContext } from "./NavRailItem";
import { SidebarInboxButton } from "./sidebar/SidebarInboxButton";
import {
  AppsRailIcon,
  ChatRailIcon,
  GovernanceRailIcon,
  HomeRailIcon,
  SettingsRailIcon,
  SpacesRailIcon,
} from "./icons/RailIcons";
import type { ComponentType } from "react";
import { useLocation } from "@tanstack/react-router";

import { useLocalStorage } from "../hooks/useLocalStorage";
import { useClientSettings } from "../hooks/useSettings";
import { isAppsPathname } from "./apps/appsPaths";
import { isHomePathname, isSpacesPathname, isWorkflowsPathname } from "./home/homePaths";
import { isInboxPathname } from "./inbox/inboxPaths";
import { isRegulationPathname } from "./regulation/regulationPaths";
import { isSettingsPathname } from "./settings/settingsPaths";
import { cn } from "../lib/utils";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { WorkspaceUserMenu } from "./auth/WorkspaceUserMenu";
import { HelpMenu } from "./HelpMenu";
import { orgUrlSegment } from "../orgRouting";
import { useActiveOrgId, useActiveOrg } from "../state/orgs";
import { useComplianceOverviewPrefetch } from "../hooks/useComplianceOverviewPrefetch";
import { OrgLogo, OrgMenuItems, useOrgSelection } from "./OrgSwitcher";
import { Menu, MenuPopup, MenuTrigger } from "./ui/menu";
import { Button } from "./ui/button";
import { SidebarTrigger, useSidebar } from "./ui/sidebar";
import { Collapsible, CollapsiblePanel } from "./ui/collapsible";
import { Tooltip, TooltipPopup, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

/**
 * The rail's width: wide enough for the labels under the icons ("Governance"
 * included) in both icon sizes. Keep in sync with `--nav-rail-width` in index.css.
 */
export const NAV_RAIL_WIDTH = 68;

const RAIL_TOOLTIP_DELAY = 150;

export type RailSectionKey =
  | "home"
  | "chat"
  | "regulation"
  | "apps"
  | "spaces"
  | "workflows"
  | "settings"
  | "inbox";

/**
 * The rail's destinations, Home first. Every one carries the company you are
 * in (`org`); the org-less fallback is only for when no company is known yet.
 */
const RAIL_SECTIONS: ReadonlyArray<{
  key: "home" | "chat" | "regulation";
  label: string;
  icon: ComponentType<{ className?: string }>;
  to: { readonly org: "/$org" | "/$org/chat" | "/$org/compliance"; readonly fallback: "/" | "/compliance" };
  /** Anchor for the first-run tour (`data-tour`). */
  tourId: string;
}> = [
  { key: "home", label: "Home", icon: HomeRailIcon, to: { org: "/$org", fallback: "/" }, tourId: "nav-home" },
  { key: "chat", label: "Chat", icon: ChatRailIcon, to: { org: "/$org/chat", fallback: "/" }, tourId: "nav-chat" },
  {
    key: "regulation",
    // "Govern" on the rail only; the pages, cards and menus say "Governance".
    label: "Govern",
    icon: GovernanceRailIcon,
    to: { org: "/$org/compliance", fallback: "/compliance" },
    tourId: "nav-governance",
  },
];

export function resolveActiveRailSection(pathname: string): RailSectionKey {
  if (isSettingsPathname(pathname)) return "settings";
  if (isRegulationPathname(pathname)) return "regulation";
  if (isInboxPathname(pathname)) return "inbox";
  if (isAppsPathname(pathname)) return "apps";
  // Spaces and Workflows are Home pages with their own rail items, so they are asked before Home.
  if (isSpacesPathname(pathname)) return "spaces";
  if (isWorkflowsPathname(pathname)) return "workflows";
  if (isHomePathname(pathname)) return "home";
  return "chat";
}

/**
 * The tenant mark at the top of the rail. The selected org scopes chat,
 * regulation and settings alike, so the switcher lives on the app-global rail
 * (the mobile chat sidebar keeps its own row — the rail is hidden below md).
 */
function NavRailOrgSwitcher({ bigger }: { bigger: boolean }) {
  const { orgs, activeOrg, available, selectOrg } = useOrgSelection();
  const [open, setOpen] = useState(false);

  if (!available) {
    return null;
  }

  // No name under the mark — the logo itself is the label, larger on the
  // bigger-icons rail; the tooltip carries the org name in both modes.
  const trigger = (
    <MenuTrigger
      aria-label={activeOrg ? `Switch organization — ${activeOrg.name}` : "Switch organization"}
      data-active={open || undefined}
      className={cn(
        RAIL_ITEM_BASE_CLASS,
        bigger ? "size-11 rounded-lg" : "size-8 rounded-[var(--control-radius)]",
      )}
    >
      <OrgLogo org={activeOrg ?? null} surface="frame" className={bigger ? "size-7" : "size-5"} />
    </MenuTrigger>
  );

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger delay={RAIL_TOOLTIP_DELAY} render={trigger} />
        <TooltipPopup side="right">{activeOrg?.name ?? "Switch organization"}</TooltipPopup>
      </Tooltip>
      <MenuPopup side="right" align="start" className="min-w-56">
        <OrgMenuItems orgs={orgs} activeOrgId={activeOrg?.id ?? null} onSelect={selectOrg} />
      </MenuPopup>
    </Menu>
  );
}

/**
 * A step up from the titlebar control, a step below the rail items: the toggle
 * is a utility, not a destination, so it should not read as one of them.
 */
const RAIL_TOGGLE_BIGGER_CLASS = "size-9! rounded-lg [&_svg]:size-[18px]!";

function RailToggle({
  className: classNameProp,
  inboxSidebar = false,
  bigger = false,
}: {
  className?: string;
  inboxSidebar?: boolean;
  bigger?: boolean;
}) {
  const className = cn(classNameProp, bigger && RAIL_TOGGLE_BIGGER_CLASS);
  const { open, setOpen } = useSidebar();
  const label = inboxSidebar
    ? open
      ? "Close inbox sidebar"
      : "Open inbox sidebar"
    : "Toggle main sidebar";
  return (
    <Tooltip>
      <TooltipTrigger
        delay={RAIL_TOOLTIP_DELAY}
        render={
          inboxSidebar ? (
            <Button
              aria-label={label}
              aria-expanded={open}
              aria-controls="inbox-sidebar"
              size="icon"
              variant="ghost"
              className={cn(
                "size-[var(--workspace-titlebar-control-size)]! [-webkit-app-region:no-drag]",
                className,
              )}
              onClick={() => setOpen(!open)}
            >
              {open ? <PanelLeftCloseIcon /> : <PanelLeftIcon />}
            </Button>
          ) : (
            <SidebarTrigger aria-label="Toggle main sidebar" className={className} />
          )
        }
      />
      <TooltipPopup side="right">{label}</TooltipPopup>
    </Tooltip>
  );
}

const RAIL_PILL_SPRING = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Where the selection marker last rested. Some section changes remount the
 * whole layout, and a fresh rail would otherwise place its marker in one jump;
 * remembering the spot lets the new rail start there and glide.
 */
let lastMarkerRest: { top: number; height: number } | null = null;

/**
 * The rail's hover surface: one pill that glides between items, the vertical
 * twin of `ui/animated-tabs`. Same spring, same rule — the first hover
 * materialises in place, later ones travel — so the rail and the page tabs
 * move alike. Items report themselves through RailHoverContext and paint no
 * hover background of their own.
 */
function RailHoverPill({
  className,
  activeKey,
  children,
}: {
  className?: string;
  /** Changes whenever the selected item or the rail's geometry does. */
  activeKey: string;
  children: ReactNode;
}) {
  const listRef = useRef<HTMLDivElement | null>(null);
  // The selection marker: a short bar on the rail's right edge, level with the
  // selected item, that travels on the same spring as the hover pill.
  const [marker, setMarker] = useState(() =>
    lastMarkerRest
      ? { ...lastMarkerRest, visible: true, jump: false }
      : { top: 0, height: 0, visible: false, jump: true },
  );

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    if (active === null) {
      setMarker((previous) => (previous.visible ? { ...previous, visible: false } : previous));
      return;
    }
    const listRect = list.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    const height = Math.round(rect.height * 0.5);
    const top = rect.top - listRect.top + (rect.height - height) / 2;
    lastMarkerRest = { top, height };
    // Next frame, so a rail that just mounted at the remembered spot paints
    // there once before it moves; setting it now would skip the transition.
    const frame = requestAnimationFrame(() =>
      setMarker((previous) => ({
        top,
        height,
        visible: true,
      // Only the very first placement appears in place. A navigation can
      // leave one frame with nothing selected; hiding then must not reset the
      // glide, or every section change would teleport.
        jump: previous.height === 0,
      })),
    );
    return () => cancelAnimationFrame(frame);
  }, [activeKey]);

  // A folding group (More) moves the items under it without a navigation:
  // while it changes height the marker follows the selected item exactly.
  // The observer runs after layout and before paint, so the move is flushed
  // there and then: a state update left to React's scheduler lands after the
  // paint, and the marker trails the item by a frame (up to ~35px as More opens).
  useEffect(() => {
    const list = listRef.current;
    if (list === null || typeof ResizeObserver === "undefined") return;
    const follow = () => {
      const active = list.querySelector<HTMLElement>('[data-active="true"]');
      if (active === null) return;
      const listRect = list.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      const height = Math.round(rect.height * 0.5);
      const top = rect.top - listRect.top + (rect.height - height) / 2;
      // Most mutations under the rail move nothing: skip the forced render then.
      if (lastMarkerRest?.top === top && lastMarkerRest.height === height) return;
      lastMarkerRest = { top, height };
      flushSync(() =>
        setMarker((previous) =>
          previous.top === top && previous.height === height ? previous : { top, height, visible: true, jump: true },
        ),
      );
    };
    const observer = new ResizeObserver(follow);
    for (const element of list.querySelectorAll("[data-rail-resizes]")) observer.observe(element);
    // An item that comes or goes (Inbox, once the server says whether it has
    // one) moves every item under it without a navigation either.
    const items = new MutationObserver(follow);
    items.observe(list, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      items.disconnect();
    };
  }, []);

  const markerStyle: CSSProperties = {
    top: marker.top,
    height: marker.height,
    opacity: marker.visible ? 1 : 0,
    transitionProperty: "top, height, opacity",
    transitionDuration: marker.jump ? "0ms, 0ms, 200ms" : "400ms, 400ms, 300ms",
    transitionTimingFunction: `${RAIL_PILL_SPRING}, ${RAIL_PILL_SPRING}, ease-in-out`,
  };

  const [pill, setPill] = useState({ top: 0, left: 0, size: 0, radius: "", visible: false, jump: true });

  const hover = useCallback((element: HTMLElement) => {
    const list = listRef.current;
    if (list === null) return;
    const listRect = list.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    setPill((previous) => ({
      top: rect.top - listRect.top,
      left: rect.left - listRect.left,
      size: rect.height,
      radius: getComputedStyle(element).borderRadius,
      visible: true,
      jump: !previous.visible,
    }));
  }, []);

  const leave = useCallback(() => {
    setPill((previous) => (previous.visible ? { ...previous, visible: false } : previous));
  }, []);

  const style: CSSProperties = {
    top: pill.top,
    left: pill.left,
    width: pill.size,
    height: pill.size,
    borderRadius: pill.radius,
    opacity: pill.visible ? 1 : 0,
    transitionProperty: "top, opacity",
    transitionDuration: pill.jump ? "0ms, 200ms" : "400ms, 300ms",
    transitionTimingFunction: `${RAIL_PILL_SPRING}, ease-in-out`,
  };

  return (
    <RailHoverContext value={hover}>
      <div ref={listRef} className={cn("relative", className)} onMouseLeave={leave}>
        <div
          aria-hidden
          data-rail-hover-pill=""
          className="pointer-events-none absolute z-0 bg-sidebar-row-hover motion-reduce:transition-none"
          style={style}
        />
        <div
          aria-hidden
          data-rail-active-marker=""
          className="pointer-events-none absolute right-1 z-[1] w-[3px] rounded-full bg-primary motion-reduce:transition-none"
          style={markerStyle}
        />
        {children}
      </div>
    </RailHoverContext>
  );
}

/**
 * Under Govern: "More" unfolds Workflows above it and becomes "Less"; one
 * panel moves, the toggle stays the same item. The choice is remembered, and
 * the panel is open whenever a workflow page is, so the selected item shows.
 */
function NavRailMore({
  bigger,
  orgSegment,
  workflowsActive,
}: {
  bigger: boolean;
  orgSegment: string | null;
  workflowsActive: boolean;
}) {
  const [stored, setOpen] = useLocalStorage("amoreg:nav-rail-more:v1", false, Schema.Boolean);
  const open = stored || workflowsActive;
  return (
    <div data-rail-resizes="" className="flex w-full flex-col items-center">
      <Collapsible open={open}>
        <CollapsiblePanel keepMounted className={cn(RAIL_MORE_PANEL_CLASS, "w-full")}>
          {/* The rail's own gap, inside the panel, so it folds with it. */}
          <div className={cn("flex w-full flex-col items-center", bigger ? "pb-3" : "pb-2.5")}>
            <NavRailItem
              label="Workflows"
              icon={AnimatedWorkflowIcon}
              to={orgSegment ? "/$org/workflows" : "/"}
              params={orgSegment ? { org: orgSegment } : undefined}
              isActive={workflowsActive}
              bigger={bigger}
            />
          </div>
        </CollapsiblePanel>
      </Collapsible>
      <NavRailItem
        label={open ? "Less" : "More"}
        icon={open ? ChevronUpIcon : EllipsisIcon}
        isActive={false}
        bigger={bigger}
        expanded={open}
        onClick={() => setOpen(!open)}
      />
    </div>
  );
}

/** The timeline's fold, a touch quicker: one short item, so it should feel instant. */
const RAIL_MORE_PANEL_CLASS =
  "h-(--collapsible-panel-height) overflow-hidden opacity-100 transition-[height,opacity] duration-150 ease-out data-starting-style:h-0 data-starting-style:opacity-0 data-ending-style:h-0 data-ending-style:opacity-0 motion-reduce:transition-none";

/**
 * The persistent far-left section rail. The chat sidebar collapses and
 * resizes independently of it; the rail is the one fixed piece of chrome,
 * so it also owns the sidebar collapse toggle.
 */
export function NavRail({
  topInset,
  standalone = false,
}: {
  topInset: boolean;
  standalone?: boolean;
}) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const activeSection = resolveActiveRailSection(pathname);
  // Regulation is tenant-scoped, so the rail links into the active org.
  const activeOrgId = useActiveOrgId();
  const activeOrg = useActiveOrg();
  const prefetchOverview = useComplianceOverviewPrefetch(activeOrg?.id ?? null);
  const orgSegment = activeOrgId ? orgUrlSegment(activeOrgId) : null;
  // The setting keeps its historical key; it now means "bigger icons".
  const bigger = useClientSettings((settings) => settings.navRailLabelsEnabled);
  const itemGap = bigger ? "gap-3" : "gap-2.5";

  return (
    <nav
      aria-label="Primary"
      data-nav-rail=""
      data-rail-compact={bigger ? undefined : ""}
      // data-app-sidebar puts the rail inside the sidebar's theme token scope
      // ([data-app-sidebar] / html[data-theme-id] [data-app-sidebar]) so it is
      // always the exact same surface as the sidebar, in every theme.
      data-app-sidebar=""
      // No border: the rail sits on the window surface, and the workspace
      // inset beside it draws the only edge (see [data-shell-frame]).
      className={cn(
        "relative z-20 w-[var(--nav-rail-width)] shrink-0 flex-col items-center bg-sidebar surface-grain pb-2.5",
        standalone ? "flex max-md:w-12" : "hidden md:flex",
      )}
    >
      {/* The collapse toggle anchors the rail's topbar strip. On macOS
          desktop the traffic lights own that corner, so the strip stays an
          empty drag region and the toggle drops in below it instead. */}
      <div
        className={cn(
          // Inside the shell frame the strip is as tall as the top bar beside
          // it, so the toggle shares a centre line with the brand and search.
          "relative h-[var(--shell-top,var(--workspace-topbar-height))] w-full shrink-0",
          topInset && "drag-region",
        )}
      >
        {/* Absolute transform centering: immune to flex quirks and any
            wrapper elements a tooltip implementation might introduce. */}
        {!topInset && (
          <RailToggle
            inboxSidebar={standalone}
            bigger={bigger}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        )}
      </div>
      <TooltipProvider delay={RAIL_TOOLTIP_DELAY} closeDelay={0}>
        <RailHoverPill
          activeKey={`${pathname}:${bigger ? "big" : "compact"}:${topInset ? "inset" : "flush"}`}
          className={cn("flex min-h-0 w-full flex-1 flex-col items-center", itemGap)}
        >
          {topInset && <RailToggle inboxSidebar={standalone} bigger={bigger} />}
          {/* From md the shell's top strip owns the company switcher
              (ShellTopBar). Below md only the inbox shows the rail, and the
              strip is absent there, so the rail keeps the switcher. */}
          <div className="md:hidden">
            <NavRailOrgSwitcher bigger={bigger} />
          </div>
          {RAIL_SECTIONS.map((section) => (
            <NavRailItem
              key={section.key}
              label={section.label}
              icon={section.icon}
              // Every destination carries the company you are in. The org-less
              // paths have to guess one — chat's "/" resumes the last thread,
              // whichever company it belonged to — so moving between sections
              // used to change company underfoot. They remain only as the
              // fallback for when no company is known yet.
              to={orgSegment ? section.to.org : section.to.fallback}
              params={orgSegment ? { org: orgSegment } : undefined}
              isActive={activeSection === section.key}
              bigger={bigger}
              tourId={section.tourId}
              onIntent={section.key === "regulation" ? prefetchOverview : undefined}
            />
          ))}
          <NavRailMore bigger={bigger} orgSegment={orgSegment} workflowsActive={activeSection === "workflows"} />
          {/* Destinations above, the workspace's own places below: a hairline between. */}
          <div aria-hidden data-rail-separator="" className="h-px w-6 shrink-0 bg-sidebar-border" />
          <NavRailItem
            label="Apps"
            icon={AppsRailIcon}
            to={orgSegment ? "/$org/apps" : "/"}
            params={orgSegment ? { org: orgSegment } : undefined}
            isActive={activeSection === "apps"}
            bigger={bigger}
            tourId="nav-apps"
          />
          <NavRailItem
            label="Spaces"
            icon={SpacesRailIcon}
            to={orgSegment ? "/$org/spaces" : "/"}
            params={orgSegment ? { org: orgSegment } : undefined}
            isActive={activeSection === "spaces"}
            bigger={bigger}
            tourId="nav-spaces"
          />
          <SidebarInboxButton rail bigger={bigger} />
          <NavRailItem
            label="Settings"
            icon={SettingsRailIcon}
            to={orgSegment ? "/$org/settings/$section" : "/settings"}
            params={orgSegment ? { org: orgSegment, section: "general" } : undefined}
            isActive={activeSection === "settings"}
            bigger={bigger}
            tourId="nav-settings"
          />
          <div className="mt-auto" />
          <HelpMenu />
          {/* From md the strip above the inset carries the account, to the
              right of Ask AI; below md there is no strip, so the rail keeps it. */}
          <div className="md:hidden">
            <WorkspaceUserMenu />
          </div>
        </RailHoverPill>
      </TooltipProvider>
    </nav>
  );
}
